import React, { useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Text,
} from "@mantine/core";
import {
  IconCheck,
  IconDotsVertical,
  IconRefresh,
  IconUserMinus,
} from "@tabler/icons-react";
import { ReportShare } from "../../../types/reports";
import styles from "./ShareReportModal.module.css";

export interface ReportShareRowProps {
  share: ReportShare;
  busy: boolean;
  onSetSync: (share: ReportShare, syncWithSource: boolean) => void;
  onRevoke: (share: ReportShare) => void;
}

/**
 * One person in the Share modal's "Shared with" list: name, role and sync
 * state, with a "⋮" menu — Keep in sync (toggle) above Revoke access.
 * Revoking deletes their copy, so it asks for confirmation in place.
 */
export default function ReportShareRow({
  share,
  busy,
  onSetSync,
  onRevoke,
}: ReportShareRowProps) {
  const [confirming, setConfirming] = useState(false);
  const { recipient, sync_with_source: synced } = share;

  if (confirming) {
    return (
      <Box className={styles.row}>
        <Text size="sm">
          Revoke {recipient.name}&apos;s access? Their copy, including any
          changes they made, will be removed.
        </Text>
        <Group justify="flex-end" gap="xs" mt="xs">
          <Button
            size="xs"
            variant="default"
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="var(--color-danger)"
            loading={busy}
            onClick={() => onRevoke(share)}
          >
            Revoke
          </Button>
        </Group>
      </Box>
    );
  }

  return (
    <Group className={styles.row} justify="space-between" wrap="nowrap">
      <Box miw={0}>
        <Text size="sm" fw={500} truncate>
          {recipient.name}
        </Text>
        <Group gap={6}>
          <Text size="xs" c="dimmed">
            {recipient.role}
          </Text>
          {synced && (
            <Badge size="xs" variant="light" color="teal">
              Synced
            </Badge>
          )}
        </Group>
      </Box>
      {busy ? (
        <Loader size="xs" aria-label={`Updating ${recipient.name}`} />
      ) : (
        <Menu position="bottom-end" shadow="md" radius="var(--radius-lg)">
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Sharing options for ${recipient.name}`}
            >
              <IconDotsVertical size={16} aria-hidden />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconRefresh size={15} aria-hidden />}
              rightSection={
                synced ? <IconCheck size={15} aria-label="On" /> : null
              }
              onClick={() => onSetSync(share, !synced)}
            >
              Keep in sync
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              className={styles.danger}
              leftSection={<IconUserMinus size={15} aria-hidden />}
              onClick={() => setConfirming(true)}
            >
              Revoke access
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}
